import type { WsNotificationEvent } from "@/types/notification";
import { appConfig } from "@/lib/config";

export type WsStatus = "connecting" | "connected" | "disconnected";

// ==========================================
// Per-type notification subscription registry
// ==========================================

type NotificationHandler = (payload: unknown) => void;
const _notificationHandlers = new Map<string, Set<NotificationHandler>>();

/**
 * Subscribe to a specific notification type delivered over the WS channel.
 * Returns an unsubscribe function — call it in `useEffect` cleanup.
 *
 * The handler receives the raw `WsNotificationEvent` as its payload so
 * callers can read `metadata`, `title`, etc. in addition to the event type.
 *
 * Usage:
 *   const unsub = subscribeNotification("walkin_promotion_suggested", handler);
 *   // later:
 *   unsub();
 */
export function subscribeNotification(
  type: string,
  handler: NotificationHandler,
): () => void {
  let set = _notificationHandlers.get(type);
  if (!set) {
    set = new Set();
    _notificationHandlers.set(type, set);
  }
  set.add(handler);
  return () => {
    set?.delete(handler);
  };
}

/**
 * Dispatch a received WS event to all per-type subscribers.
 * Called by the NotificationProvider's onMessage handler.
 */
export function dispatchNotificationEvent(event: WsNotificationEvent): void {
  const set = _notificationHandlers.get(event.type);
  if (set) {
    set.forEach((h) => h(event));
  }
}

/** Consecutive 4003 closes (with fresh tokens) after which reconnecting stops. */
const MAX_AUTH_REJECTIONS = 5;

export interface NotificationWsOptions {
  /**
   * Returns a currently valid access token (refreshing it if needed), or null
   * when the user is signed out. Called before every (re)connect: access
   * tokens expire after 30 minutes, so a token captured once goes stale and
   * every later reconnect would be rejected.
   */
  getAccessToken: () => Promise<string | null>;
  /**
   * Whether a refresh token is still stored. When getAccessToken() yields
   * nothing but this is true (e.g. the refresh hit a 5xx or the network was
   * down), the socket keeps retrying with backoff instead of staying down.
   */
  hasRefreshToken?: () => boolean;
  outletId: string;
  profileId: string;
  onMessage: (event: WsNotificationEvent) => void;
  onStatusChange: (status: WsStatus) => void;
}

/**
 * Manages a WebSocket connection for real-time notifications.
 * Handles heartbeat pings, exponential-backoff reconnection,
 * and graceful teardown.
 */
export class NotificationWebSocket {
  private ws: WebSocket | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = 1000;
  /** Consecutive 4003 (auth rejected) closes, each with a freshly obtained token. */
  private authRejections = 0;
  private disposed = false;
  private options: NotificationWsOptions;

  constructor(options: NotificationWsOptions) {
    this.options = options;
  }

  /** Open the WebSocket connection with a fresh access token. */
  async connect() {
    if (this.disposed) return;
    this.cleanup();

    let token: string | null = null;
    try {
      token = await this.options.getAccessToken();
    } catch {
      token = null;
    }
    if (this.disposed) return;
    if (!token) {
      this.options.onStatusChange("disconnected");
      // Refresh failed for a transient reason (refresh token still there): retry.
      // Truly signed out: stay down until the provider reconnects us.
      if (this.options.hasRefreshToken?.()) this.scheduleReconnect();
      return;
    }

    const url = `${appConfig.wsBaseUrl}/api/v1/ws/notifications?token=${encodeURIComponent(token)}&outlet_id=${encodeURIComponent(this.options.outletId)}&profile_id=${encodeURIComponent(this.options.profileId)}`;

    this.options.onStatusChange("connecting");

    try {
      this.ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.options.onStatusChange("connected");
      this.retryDelay = 1000; // reset backoff on success
      this.authRejections = 0;
      this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      const data = event.data as string;
      if (data === "pong") return; // heartbeat response

      try {
        const parsed: WsNotificationEvent = JSON.parse(data);
        if (parsed.id) {
          this.options.onMessage(parsed);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    this.ws.onclose = (event) => {
      this.options.onStatusChange("disconnected");
      this.stopHeartbeat();

      // Auth rejections (4001/4003, or 1006 when the server refuses the
      // handshake) are retried too: the next connect() fetches a fresh token.
      // But a server that keeps refusing fresh tokens won't change its mind:
      // stop after MAX_AUTH_REJECTIONS in a row.
      if (event?.code === 4003) {
        this.authRejections += 1;
        if (this.authRejections >= MAX_AUTH_REJECTIONS) return;
      }
      if (!this.disposed) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // onclose will fire after onerror, which handles reconnect
    };
  }

  /** Permanently close the connection and cancel all timers. */
  disconnect() {
    this.disposed = true;
    this.cleanup();
    if (this.ws) {
      // Nothing from this socket reaches the app any more (a switch: it was the last person's).
      this.ws.onmessage = null;
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }
    this.options.onStatusChange("disconnected");
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send("ping");
      }
    }, 20_000);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect() {
    if (this.disposed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    // Up to +30% jitter so many terminals don't reconnect in lockstep after a redeploy.
    const delay = Math.round(this.retryDelay * (1 + Math.random() * 0.3));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
      this.retryDelay = Math.min(this.retryDelay * 2, 30_000);
    }, delay);
  }

  private cleanup() {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
